import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// Renders an HCBS rubric evaluation: the Service Strategies and the
// person-centered indicators. The global rubric has 5 and 3; a structured
// custom prompt (built in the MIS app) sets its own, so neither count nor the
// indicator keys are assumed here.
//
// Shared because the case note and a form are now scored by the same rubric on
// the API side (helpers/hcbsRubric.js) and therefore come back in the same
// shape. Rendering them twice would be two things to keep in step for no
// benefit -- and the score a DSP sees should look the same wherever they wrote.
//
// Goes inside <GuidanceWindow>, which owns the frame, the score chip and tips.

const strategyChip = (status) => {
    if (status === 'Covered') return { icon: <CheckCircleOutlineIcon />, color: 'success' };
    if (status === 'Partially Covered') return { icon: <WarningAmberIcon />, color: 'warning' };
    return { icon: <CancelOutlinedIcon />, color: 'error' };
};

const indicatorChip = (status) => {
    if (status === 'Captured') return { icon: <CheckCircleOutlineIcon />, color: 'success' };
    return { icon: <CancelOutlinedIcon />, color: 'error' };
};

// Indicator keys are the snake_cased names set in the prompt: "behavioral_triggers" -> "Behavioral Triggers".
const indicatorLabel = (key) => key
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const sectionLabel = (text) => (
    <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
        {text}
    </Typography>
);

/**
 * @param {Object} result the guidance payload — { strategies, person_centered_indicators }
 */
const HcbsGuidanceSections = ({ result }) => {
    const strategies = result?.strategies || [];
    const indicators = Object.entries(result?.person_centered_indicators || {});

    // "Partially Covered" counts toward the tally: the caption reports how much
    // of the rubric the writing touches at all, not how much it aced.
    const coveredCount = strategies.filter((s) => s.status === 'Covered' || s.status === 'Partially Covered').length;
    const capturedCount = indicators.filter(([, ind]) => ind?.status === 'Captured').length;

    return (
        <>
            {sectionLabel(`Service Strategies (${coveredCount}/${strategies.length})`)}
            <Box sx={{ mt: 0.75 }}>
                {strategies.map((s) => {
                    const { icon, color } = strategyChip(s.status);
                    return (
                        <Box key={s.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1 }}>
                            {React.cloneElement(icon, { fontSize: 'small', color })}
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                                    {s.name}
                                </Typography>
                                {s.evidence && (
                                    <Typography variant="caption" sx={{ display: 'block', color: '#64748b', lineHeight: 1.35 }}>
                                        {s.evidence}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {indicators.length > 0 && (
                <>
                    <Divider sx={{ my: 1.5 }} />

                    {sectionLabel(`Person-Centered Details (${capturedCount}/${indicators.length})`)}
                    <Box sx={{ mt: 0.75 }}>
                        {indicators.map(([key, ind]) => {
                            const { icon, color } = indicatorChip(ind?.status);
                            return (
                                <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.75 }}>
                                    {React.cloneElement(icon, { fontSize: 'small', color })}
                                    <Typography variant="body2" sx={{ color: '#334155' }}>
                                        <strong>{indicatorLabel(key)}:</strong> {ind?.value || 'Not specified'}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}
        </>
    );
};

export default HcbsGuidanceSections;
